// Navbar.jsx
import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../context/CartContext';

export default function Navbar() {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
const { cartCount, setIsCartOpen } = useCart();
  return (
    <nav 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 100, damping: 20 }}
      className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <span className="text-2xl font-black tracking-widest text-white group-hover:text-[#CC0000] transition-colors duration-300">BROLAX</span>
          <span className="bg-[#CC0000] text-white px-2 py-0.5 rounded text-xs font-black tracking-wider">BX</span>
        </Link>

        {/* Desktop Links */}
        <ul className="hidden md:flex gap-10">
          <li>
            <NavLink to="/" end className={({ isActive }) =>
              `relative text-sm font-bold transition-colors duration-300 ${isActive ? 'text-white' : 'text-neutral-400 hover:text-white'}`
            }>
              {({ isActive }) => (
                <>
                  الرئيسية
                  {isActive && (
                    <motion.div layoutId="nav-underline" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#CC0000]" />
                  )}
                </>
              )}
            </NavLink>
          </li>
          <li>
            <NavLink to="/shop" className={({ isActive }) =>
              `relative text-sm font-bold transition-colors duration-300 ${isActive ? 'text-white' : 'text-neutral-400 hover:text-white'}`
            }>
              {({ isActive }) => (
                <>
                  المتجر
                  {isActive && (
                    <motion.div layoutId="nav-underline" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#CC0000]" />
                  )}
                </>
              )}
            </NavLink>
          </li>
        </ul>

        {/* Right Actions */}
        <div className="flex items-center gap-6">
          <NavLink to="/track" className="hidden md:flex items-center gap-2 text-neutral-400 text-sm font-bold hover:text-white transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>
            تتبع الطلب
          </NavLink>
          
<button
  onClick={() => setIsCartOpen(true)}
  className="relative p-2 text-white hover:text-[#CC0000] transition-colors group"
>            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <AnimatePresence>
              {cartCount > 0 && (
                <motion.span 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute -top-1 -right-1 bg-[#CC0000] text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full font-black shadow-lg shadow-red-900/50"
                >
                  {cartCount}
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          {/* Hamburger */}
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="md:hidden flex flex-col gap-1.5 p-2 focus:outline-none z-50"
          >
            <motion.span 
              animate={isMobileOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
              className="block w-6 h-0.5 bg-white transition-colors"
            />
            <motion.span 
              animate={isMobileOpen ? { opacity: 0 } : { opacity: 1 }}
              className="block w-6 h-0.5 bg-white"
            />
            <motion.span 
              animate={isMobileOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
              className="block w-6 h-0.5 bg-white transition-colors"
            />
          </button>
        </div>
      </div>

{/* Mobile Menu */}
<AnimatePresence>
  {isMobileOpen && (
    <motion.div 
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="md:hidden bg-neutral-950 border-t border-white/10 overflow-hidden"
    >
      <div className="px-6 py-6 space-y-4">
        {[
          { to: '/', label: 'الرئيسية', type: 'link' },
          { to: '/shop', label: 'المتجر', type: 'link' },
          { label: 'سلة المشتريات', type: 'cart' },           // ← opens drawer
          { to: '/track', label: 'تتبع الطلب', type: 'link' },
        ].map((item) =>
          item.type === 'cart' ? (
            <button
              key="cart-mobile"
              onClick={() => {
                setIsMobileOpen(false);
                setIsCartOpen(true);
              }}
              className="block w-full text-right text-lg font-bold text-neutral-300 hover:text-white transition-colors"
            >
              {item.label}
              {cartCount > 0 && (
                <span className="mr-2 inline-flex items-center justify-center w-5 h-5 bg-[#CC0000] text-white text-xs rounded-full">
                  {cartCount}
                </span>
              )}
            </button>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setIsMobileOpen(false)}
              className={({ isActive }) =>
                `block text-lg font-bold transition-colors ${
                  isActive ? 'text-[#CC0000]' : 'text-neutral-300 hover:text-white'
                }`
              }
            >
              {item.label}
            </NavLink>
          )
        )}
      </div>
    </motion.div>
  )}
</AnimatePresence>
    </nav>
  );
}